import { useEffect } from "react";
import { createPortal } from "react-dom";
import { NavLink, useNavigate } from "react-router-dom";
import { getVanillaEl } from "../../domBridge";
import { requestLock } from "../../../lockBus";

// Replaces nav.ts entirely (Phase 10: nav/routing consolidation) - portals
// into the existing #appTabBar (its 5 static buttons cleared from
// index.html) and wires the header's #brandBadge, both still vanilla-owned
// elements reached via getVanillaEl, same trigger-element pattern as every
// other phase. "Add" is a real route now (/add - AddScreen.tsx, the
// dedicated destination page replacing the old album-picker modal), so it
// gets the same NavLink/active-state treatment as Chat/Albums/Music. "Lock"
// stays a plain action button - it doesn't correspond to a screen.
function tabClassName({ isActive }: { isActive: boolean }): string {
  return isActive ? "app-tab active" : "app-tab";
}

// Add gets a visually distinct always-filled, slightly raised circular
// treatment (not just the shared .active pill every other tab gets) -
// matching the reference's central FAB-like emphasis. Still built from the
// same NavLink/tabClassName mechanism, not a separate nav architecture.
function addTabClassName({ isActive }: { isActive: boolean }): string {
  return isActive ? "app-tab app-tab-fab active" : "app-tab app-tab-fab";
}

function AppNavBody() {
  const navigate = useNavigate();

  useEffect(() => {
    const brandBadge = getVanillaEl<HTMLButtonElement>("brandBadge");
    if (!brandBadge) return;
    const onClick = () => navigate("/");
    brandBadge.addEventListener("click", onClick);
    return () => brandBadge.removeEventListener("click", onClick);
  }, [navigate]);

  // Was ProfileDialog.tsx's job (setOpen(true) on click) before Profile
  // became a real route - #profileBtn itself is unchanged, still a static
  // vanilla-owned header button (index.html).
  useEffect(() => {
    const profileBtn = getVanillaEl<HTMLButtonElement>("profileBtn");
    if (!profileBtn) return;
    const onClick = () => navigate("/profile");
    profileBtn.addEventListener("click", onClick);
    return () => profileBtn.removeEventListener("click", onClick);
  }, [navigate]);

  const tabBar = getVanillaEl<HTMLElement>("appTabBar");
  if (!tabBar) return null;

  return createPortal(
    <>
      {/* .app-tab resets button appearance but not an anchor's default
          underline/color - NavLink renders <a>, not <button>, and this
          portal target (#appTabBar) lives outside #react-root's DOM
          subtree, so ScopedCssBaseline's reset doesn't reach it either.
          Explicit textDecoration keeps these visually identical to the
          plain-button tabs they replace. */}
      <NavLink to="/chat" className={tabClassName} aria-label="Chat" style={{ textDecoration: "none" }}>
        <span className="app-tab-icon">
          <i className="bi bi-chat-dots-fill" aria-hidden="true" />
          <span id="chatTabDot" className="app-tab-dot d-none" aria-hidden="true" />
        </span>
        <span className="app-tab-label">Chat</span>
      </NavLink>
      <NavLink to="/albums" className={tabClassName} aria-label="Albums" style={{ textDecoration: "none" }}>
        <span className="app-tab-icon">
          <i className="bi bi-collection-fill" aria-hidden="true" />
        </span>
        <span className="app-tab-label">Albums</span>
      </NavLink>
      <NavLink to="/add" className={addTabClassName} aria-label="Add" style={{ textDecoration: "none" }}>
        <span className="app-tab-icon">
          <i className="bi bi-plus-circle-fill" aria-hidden="true" />
        </span>
        <span className="app-tab-label">Add</span>
      </NavLink>
      <NavLink to="/music" className={tabClassName} aria-label="Music" style={{ textDecoration: "none" }}>
        <span className="app-tab-icon">
          <i className="bi bi-music-note-beamed" aria-hidden="true" />
        </span>
        <span className="app-tab-label">Music</span>
      </NavLink>
      <button type="button" className="app-tab" aria-label="Lock gallery" onClick={() => requestLock("manual")}>
        <span className="app-tab-icon">
          <i className="bi bi-lock-fill" aria-hidden="true" />
        </span>
        <span className="app-tab-label">Lock</span>
      </button>
    </>,
    tabBar,
  );
}

export function AppNav() {
  return <AppNavBody />;
}
